import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getUsers } from '@/data/users/getUsers'
import { createUser } from '@/data/users/createUser'
import { suggestUserColours } from '@/data/users/suggestUserColours'
import { queryKeys } from '@/lib/queryKeys'
import { GOVUK_COLOURS } from '@/components/govuk/colours'
import { useCurrentUser } from './CurrentUserContext'

export function UserPickerModal() {
  const { setCurrentUser } = useCurrentUser()
  const queryClient = useQueryClient()

  const [showAdd, setShowAdd] = useState(false)
  const [newName, setNewName] = useState('')
  const [colourHint, setColourHint] = useState('')
  const [colourOptions, setColourOptions] = useState<string[]>([])
  const [selectedColour, setSelectedColour] = useState<string | null>(null)

  const { data: users = [], isLoading } = useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: () => getUsers(),
  })

  const suggestMutation = useMutation({
    mutationFn: () =>
      suggestUserColours({
        data: { name: newName.trim(), colourHint: colourHint.trim() || undefined },
      }),
    onSuccess: ({ colours }) => {
      setColourOptions(colours)
      setSelectedColour(colours[0] ?? null)
    },
  })

  const createMutation = useMutation({
    mutationFn: () =>
      createUser({ data: { name: newName.trim(), colour: selectedColour! } }),
    onSuccess: (updatedUsers) => {
      queryClient.setQueryData(queryKeys.users.list(), updatedUsers)
      setCurrentUser(newName.trim())
    },
  })

  function resetAdd() {
    setShowAdd(false)
    setNewName('')
    setColourHint('')
    setColourOptions([])
    setSelectedColour(null)
  }

  const canSuggest = !!newName.trim() && !suggestMutation.isPending && !createMutation.isPending
  const canCreate = !!selectedColour && !createMutation.isPending

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: '4px',
          padding: '32px',
          width: '100%',
          maxWidth: '420px',
          margin: '16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        }}
      >
        <h2
          style={{
            fontSize: '24px',
            fontWeight: 700,
            marginTop: 0,
            marginBottom: '8px',
            fontFamily: 'inherit',
          }}
        >
          Hello Comrade ✊
        </h2>
        <p
          style={{
            color: GOVUK_COLOURS.darkGrey,
            marginTop: 0,
            marginBottom: '24px',
            fontSize: '15px',
          }}
        >
          Please select your name from the list, or add yourself if you're new.
        </p>

        {isLoading ? (
          <p style={{ color: GOVUK_COLOURS.darkGrey, fontSize: '14px' }}>Loading…</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
            {users.map((user) => (
              <button
                type="button"
                key={user.name}
                onClick={() => setCurrentUser(user.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  border: `2px solid ${GOVUK_COLOURS.midGrey}`,
                  borderRadius: '4px',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  transition: 'border-color 0.1s',
                }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLButtonElement).style.borderColor = user.colour
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLButtonElement).style.borderColor = GOVUK_COLOURS.midGrey
                }}
              >
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: user.colour,
                    flexShrink: 0,
                    display: 'inline-block',
                  }}
                />
                {user.name}
              </button>
            ))}
          </div>
        )}

        {/* Add new person */}
        {!showAdd ? (
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: GOVUK_COLOURS.blue,
              fontSize: '15px',
              padding: 0,
              textDecoration: 'underline',
              fontFamily: 'inherit',
            }}
          >
            + Add new person
          </button>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (colourOptions.length === 0) {
                if (canSuggest) suggestMutation.mutate()
              } else if (canCreate) {
                createMutation.mutate()
              }
            }}
            style={{
              borderTop: `1px solid ${GOVUK_COLOURS.midGrey}`,
              paddingTop: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px',
            }}
          >
            {/* Name */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontWeight: 600, fontSize: '15px', fontFamily: 'inherit' }}>
                Name
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => {
                  setNewName(e.target.value)
                  setColourOptions([])
                  setSelectedColour(null)
                }}
                placeholder="Enter name…"
                style={{
                  padding: '8px 10px',
                  border: `2px solid ${GOVUK_COLOURS.midGrey}`,
                  borderRadius: '2px',
                  fontSize: '16px',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
            </div>

            {/* Colour preference */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontWeight: 600, fontSize: '15px', fontFamily: 'inherit' }}>
                Colour preference
              </label>
              <input
                type="text"
                value={colourHint}
                onChange={(e) => {
                  setColourHint(e.target.value)
                  setColourOptions([])
                  setSelectedColour(null)
                }}
                placeholder="e.g. blue, something warm, forest green…"
                style={{
                  padding: '8px 10px',
                  border: `2px solid ${GOVUK_COLOURS.midGrey}`,
                  borderRadius: '2px',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
              <span style={{ fontSize: '13px', color: GOVUK_COLOURS.darkGrey }}>
                AI will suggest options based on this
              </span>
            </div>

            {/* Suggest button */}
            {colourOptions.length === 0 && (
              <button
                type="submit"
                disabled={!canSuggest}
                style={{
                  padding: '10px 18px',
                  backgroundColor: canSuggest ? GOVUK_COLOURS.blue : GOVUK_COLOURS.midGrey,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '2px',
                  cursor: canSuggest ? 'pointer' : 'default',
                  fontSize: '15px',
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  alignSelf: 'flex-start',
                }}
              >
                {suggestMutation.isPending ? 'Getting options…' : 'Get colour options'}
              </button>
            )}

            {/* Colour swatches */}
            {colourOptions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontWeight: 600, fontSize: '15px', fontFamily: 'inherit' }}>
                    Pick a colour
                  </span>
                  <button
                    type="button"
                    onClick={() => suggestMutation.mutate()}
                    disabled={!canSuggest}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: canSuggest ? 'pointer' : 'default',
                      color: canSuggest ? GOVUK_COLOURS.blue : GOVUK_COLOURS.midGrey,
                      fontSize: '13px',
                      padding: 0,
                      textDecoration: 'underline',
                      fontFamily: 'inherit',
                    }}
                  >
                    {suggestMutation.isPending ? 'Loading…' : 'Try again'}
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {colourOptions.map((colour) => (
                    <button
                      type="button"
                      key={colour}
                      onClick={() => setSelectedColour(colour)}
                      title={colour}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: colour,
                        border: selectedColour === colour
                          ? '3px solid #0b0c0c'
                          : '3px solid transparent',
                        outline: selectedColour === colour ? `2px solid ${colour}` : 'none',
                        outlineOffset: 2,
                        cursor: 'pointer',
                        padding: 0,
                        flexShrink: 0,
                        transition: 'border-color 0.1s, outline 0.1s',
                      }}
                    />
                  ))}
                </div>
                {selectedColour && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: '50%',
                        backgroundColor: selectedColour,
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: '14px', color: selectedColour, fontWeight: 600 }}>
                      {newName.trim()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {colourOptions.length > 0 && (
                <button
                  type="submit"
                  disabled={!canCreate}
                  style={{
                    padding: '10px 18px',
                    backgroundColor: canCreate ? GOVUK_COLOURS.green : GOVUK_COLOURS.midGrey,
                    color: '#fff',
                    border: 'none',
                    borderRadius: '2px',
                    cursor: canCreate ? 'pointer' : 'default',
                    fontSize: '16px',
                    fontWeight: 600,
                    fontFamily: 'inherit',
                  }}
                >
                  {createMutation.isPending ? 'Adding…' : 'Add & select'}
                </button>
              )}
              <button
                type="button"
                onClick={resetAdd}
                style={{
                  padding: '10px 14px',
                  background: 'none',
                  border: `1px solid ${GOVUK_COLOURS.midGrey}`,
                  borderRadius: '2px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
